<template>
  <div class="modal-overlay" @click="emit('close')">
    <div class="modal-content card" @click.stop>
      <button class="close-button" @click="emit('close')" aria-label="Close">
        ×
      </button>

      <div class="role-header">
        <h2>{{ t(role.nameKey) }}</h2>
        <span :class="['badge', `badge-${role.type}`]">
          {{ t(`roleTypes.${role.type}`) }}
        </span>
      </div>

      <div class="role-description">
        <p>{{ t(role.descriptionKey) }}</p>
      </div>

      <div class="ability-section">
        <h3>{{ t('myRole.ability') }}</h3>
        <p class="ability-text">{{ t(role.abilityKey) }}</p>
      </div>

      <div v-if="role.rulesKeys.length > 0" class="rules-section">
        <h3>{{ t('myRole.relevantRules') }}</h3>
        <ul>
          <li v-for="(ruleKey, index) in role.rulesKeys" :key="index">
            {{ t(ruleKey) }}
          </li>
        </ul>
      </div>

      <div class="night-order">
        <div v-if="role.firstNightOrder" class="night-info">
          <span class="night-label">First Night:</span>
          <span class="night-value">{{ role.firstNightOrder }}</span>
        </div>
        <div v-if="role.otherNightOrder" class="night-info">
          <span class="night-label">Other Nights:</span>
          <span class="night-value">{{ role.otherNightOrder }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { Role } from '@botc/shared';

interface Props {
  role: Role;
}

defineProps<Props>();

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: var(--spacing-md);
}

.modal-content {
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  background: linear-gradient(135deg, var(--color-bg-secondary), var(--color-bg-tertiary));
}

.close-button {
  position: absolute;
  top: var(--spacing-md);
  right: var(--spacing-md);
  background: none;
  border: none;
  color: var(--color-text-muted);
  font-size: 2rem;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color var(--transition-fast);
}

.close-button:hover {
  color: var(--color-text-primary);
}

.role-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-lg);
  padding-right: var(--spacing-xl);
}

.role-header h2 {
  margin: 0;
  color: var(--color-accent-gold);
  font-size: 1.75rem;
}

.role-description {
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-md);
  background-color: var(--color-bg-primary);
  border-radius: var(--radius-md);
  border-left: 3px solid var(--color-accent-gold);
}

.role-description p {
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
}

.ability-section,
.rules-section {
  margin-bottom: var(--spacing-xl);
}

.ability-section h3,
.rules-section h3 {
  color: var(--color-accent-gold);
  margin-bottom: var(--spacing-md);
  font-size: 1.25rem;
}

.ability-text {
  color: var(--color-text-primary);
  line-height: 1.6;
  padding: var(--spacing-md);
  background-color: var(--color-bg-primary);
  border-radius: var(--radius-md);
}

.rules-section ul {
  list-style: none;
  padding: 0;
}

.rules-section li {
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-sm);
  background-color: var(--color-bg-primary);
  border-radius: var(--radius-sm);
  border-left: 3px solid var(--color-accent-red);
  color: var(--color-text-secondary);
  line-height: 1.5;
}

.night-order {
  display: flex;
  gap: var(--spacing-lg);
  padding-top: var(--spacing-lg);
  border-top: 1px solid var(--color-bg-tertiary);
}

.night-info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.night-label {
  color: var(--color-text-muted);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.night-value {
  color: var(--color-accent-gold);
  font-size: 1.5rem;
  font-weight: 600;
}

@media (max-width: 768px) {
  .modal-content {
    max-height: 95vh;
  }

  .role-header h2 {
    font-size: 1.5rem;
  }
}
</style>
